import { requestAudioPlayback, requestVideoPlayback } from "../actions/mediaActions";
import type { PrototypeState } from "../controllerState";
import type { Root } from "../prototypeTypes";
import type { MountedPlayer } from '../mediaPlayerTypes';
import { showPlayer } from "../playerUiController";
import { playerSelectors } from "../mediaPlayerSelectors";
import { recordRuntimeError } from "../prototypeRuntimeSupport";
import { showToast } from "../toastController";
import { presentAccessDecision } from "../presentAccessDecision";
import { resolveMediaAccessDecisionForState } from '../mediaPlayerAccessGate';
import {
  closeVideoPlaybackSession,
  ensureActiveAudioTarget,
  exitDocumentFullscreen,
  isFinishedAudioTarget,
  redirectCompletedAudioTarget,
  resolveTopicResumeTarget,
  setActiveTarget,
  transferVideoProgressToAudio,
  videoShareUrl,
} from "./mediaRouteHelpers";
import {
  attachVideoPlayer,
  cycleMediaPlaybackRate,
	mountedFor,
	pauseMediaPlayer,
	playMediaPlayer,
	presentOptimisticAudioPlayback,
	rollbackOptimisticAudioPlayback,
	seekMediaPlayerBySeconds,
  toggleMediaPlayer,
  toggleVideoPictureInPicture,
} from "../mediaPlayer";
import { showArticle } from "../navigationController";
import { beginAudioHandoffCommandIntent, beginMediaUserIntent, isAudioHandoffIntentCurrent, isMediaUserIntentCurrent } from '../audioHandoffController';
import { startAudioPlaybackSingleFlight, toggleAudioPlaybackSingleFlight } from '../audioPlaybackSingleFlight';
import { beginVideoPictureInPictureExitPlaybackHandoff, hasWebKitPictureInPictureEvidence, isStandaloneWebApp, observeVideoPictureInPictureExit, videoPictureInPictureCapability } from '../videoPictureInPicture';
import { detachVideoPresentationSurface, openVideoPresentationSurface, showVideoPictureInPictureReturnSurface, undockVideoPresentationSurface } from '../videoPresentationSurface';
import { supportsNativeWebKitVideoFullscreen } from '../videoPresentationControls';

type NativeWebKitFullscreenVideo = HTMLVideoElement & {
	webkitDisplayingFullscreen?: boolean;
	webkitEnterFullscreen?: () => void;
	webkitExitFullscreen?: () => void;
};

type NativeFullscreenObserver = { captureClickIntent: () => void; cleanup: () => void };

const nativeFullscreenObservers = new WeakMap<HTMLVideoElement, NativeFullscreenObserver>();
const NATIVE_FULLSCREEN_PAUSE_CONFIRM_MS = 120;
const NATIVE_FULLSCREEN_INTENT_EVENT = 'deepmox:native-fullscreen-intent';
const NATIVE_FULLSCREEN_DISPOSE_EVENT = 'deepmox:native-fullscreen-dispose';

function observeNativeWebKitFullscreen(root: Root, media: MountedPlayer, video: HTMLVideoElement): () => void {
	const existing = nativeFullscreenObservers.get(video);
	if (existing) {
		existing.captureClickIntent();
		return existing.cleanup;
	}
	const webkit = video as NativeWebKitFullscreenVideo;
	let active = false;
	let desiredPlayback = !video.paused;
	let pendingPauseTimer: number | undefined;
	const sourceGeneration = media.sourceGeneration;
	const stillCurrent = () => mountedFor(root, 'video') === media
		&& media.element === video
		&& media.sourceGeneration === sourceGeneration;
	const cancelPendingPause = () => {
		if (pendingPauseTimer !== undefined) window.clearTimeout(pendingPauseTimer);
		pendingPauseTimer = undefined;
	};
	const captureClickIntent = () => {
		if (!active) desiredPlayback = !video.paused;
	};
	const onBegin = () => {
		active = true;
		// Keep the click-time snapshot. WebKit may emit a system pause as it
		// moves the element into its native surface, before this event arrives.
		// Once active, play/pause events below represent the actual session.
	};
	const onPlay = () => {
		cancelPendingPause();
		if (active) desiredPlayback = true;
	};
	const onPause = () => {
		if (!active) return;
		cancelPendingPause();
		// WebKit can pause before clearing webkitDisplayingFullscreen on exit.
		// Confirm a pause only if the native surface remains active briefly.
		pendingPauseTimer = window.setTimeout(() => {
			pendingPauseTimer = undefined;
			if (!active || !stillCurrent()) {
				if (!stillCurrent()) cleanup();
				return;
			}
			if (webkit.webkitDisplayingFullscreen === true) desiredPlayback = false;
		}, NATIVE_FULLSCREEN_PAUSE_CONFIRM_MS);
	};
	const onExplicitIntent = (event: Event) => {
		if (!active) return;
		cancelPendingPause();
		desiredPlayback = (event as CustomEvent<boolean>).detail === true;
	};
	const onDispose = () => cleanup();
	const onEnd = () => {
		if (!active) return;
		active = false;
		cancelPendingPause();
		if (desiredPlayback && stillCurrent()) {
			// The PiP-named helper is a generic bounded WebKit teardown handoff:
			// native fullscreen can emit pause after webkitendfullscreen as well.
			// App/user pause paths already cancel this shared handoff.
			beginVideoPictureInPictureExitPlaybackHandoff(video, () => {
				if (stillCurrent() && video.paused && !video.ended) void video.play().catch(() => undefined);
			});
		}
		cleanup();
	};
	let observer: NativeFullscreenObserver;
	const cleanup = () => {
		cancelPendingPause();
		video.removeEventListener('webkitbeginfullscreen', onBegin);
		video.removeEventListener('webkitendfullscreen', onEnd);
		video.removeEventListener('play', onPlay);
		video.removeEventListener('pause', onPause);
		video.removeEventListener(NATIVE_FULLSCREEN_INTENT_EVENT, onExplicitIntent);
		video.removeEventListener(NATIVE_FULLSCREEN_DISPOSE_EVENT, onDispose);
		if (nativeFullscreenObservers.get(video) === observer) nativeFullscreenObservers.delete(video);
	};
	video.addEventListener('webkitbeginfullscreen', onBegin);
	video.addEventListener('webkitendfullscreen', onEnd);
	video.addEventListener('play', onPlay);
	video.addEventListener('pause', onPause);
	video.addEventListener(NATIVE_FULLSCREEN_INTENT_EVENT, onExplicitIntent);
	video.addEventListener(NATIVE_FULLSCREEN_DISPOSE_EVENT, onDispose);
	observer = { captureClickIntent, cleanup };
	nativeFullscreenObservers.set(video, observer);
	return cleanup;
}

/**
 * Push a single history entry when the video fullscreen first opens so a
 * browser Back closes the video (mirroring the modal pattern) instead of
 * leaving a stale overlay while the popstate handler re-applies an article
 * route underneath it. The open state itself is the guard: re-opening after
 * a close pushes a fresh entry; in-video navigation (replay) does not.
 */
function pushVideoFullscreenHistory(root: Root): void {
  if (typeof window === 'undefined' || !window.history) return;
  const vf = root.querySelector<HTMLElement>('#videoFullscreen');
  if (!vf || typeof vf.classList?.contains !== 'function' || vf.classList.contains('active')) return;
  window.history.pushState({ videoFullscreenOpen: true }, '');
}

function shouldBlockVideoPlayback(
  root: Root,
  state: PrototypeState,
  target: { topicId: string; chapterIndex: number },
  rawAction: string,
): boolean {
  const decision = resolveMediaAccessDecisionForState(root, state, 'video', target.topicId, target.chapterIndex);
  if (decision.kind === "allow") return false;
  return presentAccessDecision(root, state, decision, {
    rawAction,
    policyAction: decision.action,
  });
}

/** Fallback only for a stale/incorrect browser capability report after a real tap. */
function offerBrowserPictureInPicturePath(state: PrototypeState): void {
	showToast({
		type: 'info',
		message: 'Picture-in-picture is unavailable in this installed app. Copy this chapter link and paste it into Safari to use PiP.',
		actionLabel: 'Copy link',
		onAction: () => {
			const url = videoShareUrl(state);
			if (!navigator.clipboard?.writeText) {
				showToast({ type: 'info', message: 'Copy is unavailable here. Copy the chapter address and paste it into Safari to use PiP.' });
				return;
			}
			void navigator.clipboard.writeText(url).then(
				() => showToast({ type: 'success', message: 'Chapter link copied. Paste it into Safari to use PiP.' }),
				() => showToast({ type: 'info', message: 'Could not copy the link. Copy the chapter address and paste it into Safari to use PiP.' }),
			);
		},
	});
}

export async function routeMediaActions(normalized: string, root: Root, state: PrototypeState, source?: HTMLElement) {
	// The transport keys are a constant row: a dimmed Listen/Watch key keeps its
	// slot and its click resolves to a hint instead of a dead playback attempt.
	if (source && (source.id === 'articleAudioBtn' || source.id === 'articleVideoBtn') && source.classList.contains('is-disabled')) {
		showToast({
			type: 'info',
			message: source.id === 'articleVideoBtn' ? 'This chapter has no video yet.' : 'This chapter has no audio yet.',
		});
		return;
	}
	if (
		normalized.includes("playTopic") ||
		normalized.includes("playArticle") ||
		normalized.includes("togglePlay") ||
		normalized.includes("showPlayer")
	) {
		// showPlayer only mounts the mini bar and paints the (still closed)
		// Now Playing content — the dock state machine decides panel visibility.
		// Skipping it on phone left playback with no mini bar or progress bar.
		// If media is already mounted and not expired, toggle play/pause instead
		// of fetching a new token and creating a new <audio> element. Reuse only
		// a source that the player itself can still play: the old 15-second route
		// threshold disagreed with the core's 30-second expiry boundary and also
		// retried a broken <audio> element forever.
		const activeAudio = mountedFor(root, 'audio');
		const alreadyMounted = Boolean(
			activeAudio?.element.src
			&& !activeAudio.element.error
			&& activeAudio.expiresAt > Date.now() / 1000 + 30,
		);
		const sameChapter = Boolean(
			activeAudio
			&& activeAudio.topicId === state.runtime.activeCourseId
			&& activeAudio.chapterIndex === state.runtime.activeChapterIndex,
		);
	if (alreadyMounted && (normalized.includes("togglePlay") || (normalized.includes("showPlayer") && sameChapter) || (normalized.includes('playTopicChapter') && sameChapter) || (normalized.includes('playArticleAudio') && sameChapter))) {
		toggleMediaPlayer(root, "audio");
	} else {
		// A finished chapter entered from this non-switch path jumps to the NEXT
		// chapter at 0:00 instead of resuming its endpoint. The mounted toggle
		// branch above keeps pause/resume semantics for the live element. The
		// finished-check is synchronous so first paint stays in the click frame;
		// only an actual redirect delays it behind the chapter-list await. An
		// unset course id means ensureActiveAudioTarget fills the target in
		// inside the flight below.
		const baseTarget = {
			topicId: state.runtime.activeCourseId ?? '',
			chapterIndex: state.runtime.activeChapterIndex ?? 0,
		};
		const target = isFinishedAudioTarget(state, baseTarget)
			? await redirectCompletedAudioTarget(root, state, baseTarget)
			: baseTarget;
		const desiredFromPending = toggleAudioPlaybackSingleFlight(root, target);
		if (desiredFromPending !== null) {
			// The pending request remains valid. Render this click immediately and
			// let its response apply the newest play/pause intent.
			presentOptimisticAudioPlayback(root, { ...target, paused: !desiredFromPending });
			showPlayer(root);
		} else {
			const intent = beginAudioHandoffCommandIntent(root);
			// This must happen before the metadata/token awaits: Article, mini and
			// Home Recent share the same optimistic projection for this click frame.
			presentOptimisticAudioPlayback(root, { ...target, paused: false });
			showPlayer(root);
			const flight = startAudioPlaybackSingleFlight(root, target, true, async (desiredPlaying) => {
				const ready = await ensureActiveAudioTarget(root, state);
				if (!ready || !isAudioHandoffIntentCurrent(root, intent)) return 'unavailable';
				return requestAudioPlayback(state.runtime, state.effectiveAccessPlan, root, recordRuntimeError, { desiredPlaying }, intent);
			});
			const result = await flight.promise;
			if (result !== 'playing' && flight.isDesiredPlaying() && isAudioHandoffIntentCurrent(root, intent)) rollbackOptimisticAudioPlayback(root, target);
		}
		}
		showPlayer(root);
	}
  // triggerVideoPaywall used to ONLY show the toast; for paid viewers
  // it now opens the actual video fullscreen so swipe + playback are
  // reachable end-to-end. The shared access resolver now keeps Free
  // chapters 1-3 playable and presents the canonical gate for chapter 4+.
	if (normalized.includes("triggerVideoPaywall") || normalized.includes("openVideoFullscreen")) {
		// A Chapter Watch belongs to the chapter being browsed. A Watch control in
		// Mini / Now Playing belongs to the mounted audio source instead: browsing
		// B must never make the global player watch B while it is still playing A.
		const audioOwner = source?.closest('#webPlayer') ? mountedFor(root, 'audio') : undefined;
		const target = audioOwner?.topicId && audioOwner.chapterIndex
			? { topicId: audioOwner.topicId, chapterIndex: audioOwner.chapterIndex }
			: { topicId: state.runtime.activeCourseId, chapterIndex: state.runtime.activeChapterIndex };
    if (shouldBlockVideoPlayback(root, state, target, normalized)) return;
		const audio = mountedFor(root, 'audio');
		if (audio?.topicId === target.topicId && audio.chapterIndex === target.chapterIndex) {
			const seconds = Number.isFinite(audio.element.currentTime) ? Math.max(0, audio.element.currentTime) : 0;
			state.runtime.mediaResumeSecondsByChapter = {
				...(state.runtime.mediaResumeSecondsByChapter ?? {}),
				[`${target.topicId}:${target.chapterIndex}`]: seconds,
			};
		}
    pauseMediaPlayer(root, "audio");
    pushVideoFullscreenHistory(root);
		openVideoPresentationSurface(root);
    attachVideoPlayer(root, state.runtime.api, target.topicId, target.chapterIndex, state.effectiveAccessPlan);
		await requestVideoPlayback(state.runtime, state.effectiveAccessPlan, root, recordRuntimeError, undefined, target);
  }
  if (normalized.includes("cycleVideoSpeed")) {
    const rate = cycleMediaPlaybackRate(root, "video");
    const label = root.querySelector<HTMLElement>("#vfSpeedBtn");
    if (label) label.textContent = `${rate.toFixed(rate % 1 === 0 ? 0 : 2).replace(/0$/, "")}x`;
  }
  if (normalized.includes("cycleAudioSpeed")) {
    const rate = cycleMediaPlaybackRate(root, "audio");
    const label = root.querySelector<HTMLElement>("#audioSpeedBtn");
    if (label) label.textContent = `${rate.toFixed(rate % 1 === 0 ? 0 : 2).replace(/0$/, "")}x`;
  }
  if (normalized.includes('seekAudioRelative')) {
    const seconds = Number(normalized.match(/seekAudioRelative\((-?\d+)/)?.[1] ?? 0);
    if (Number.isFinite(seconds) && seconds !== 0) seekMediaPlayerBySeconds(root, 'audio', seconds);
  }
  if (normalized.includes("switchVideoToAudio")) {
    await transferVideoProgressToAudio(root, state, { play: true });
  }
  if (normalized.includes("toggleVideoPlay")) {
    toggleMediaPlayer(root, "video");
  }
  if (normalized.includes("seekVideoRelative")) {
    const seconds = Number(normalized.match(/seekVideoRelative\((-?\d+)/)?.[1] ?? 0);
    if (Number.isFinite(seconds) && seconds !== 0) seekMediaPlayerBySeconds(root, "video", seconds);
  }
  if (normalized.includes("nextVideoChapter")) {
    (root as Document | HTMLElement).dispatchEvent?.(new CustomEvent("deepmox:video-next"));
  }
  if (normalized.includes("previousVideoChapter")) {
    (root as Document | HTMLElement).dispatchEvent?.(new CustomEvent("deepmox:video-prev"));
  }
  if (normalized.includes("toggleVideoFullscreen")) {
    await toggleVideoFullscreenApi(root);
  }
  if (normalized.includes("toggleVideoPip")) {
    const media = mountedFor(root, 'video');
    const video = media?.element instanceof HTMLVideoElement ? media.element : null;
    const pictureInPictureCapability = video ? videoPictureInPictureCapability(video) : 'browser_unsupported';
    if (!video || pictureInPictureCapability !== 'available') {
		const button = root.querySelector<HTMLButtonElement>('#vfPipBtn');
		if (button) {
			button.hidden = true;
			button.setAttribute('aria-hidden', 'true');
		}
      return;
    }
    let stopObservingExit: () => void = () => undefined;
    try {
		media?.pictureInPictureExitCleanup?.();
      stopObservingExit = observeVideoPictureInPictureExit(video, (wasPlaying) => {
		if (!media || mountedFor(root, 'video') !== media || media.element !== video) return;
        // The page always owns the custom controls. Native controls in the
        // inline element are what produced the duplicated Safari timeline.
        video.controls = false;
        root.querySelector<HTMLElement>('#vfPipBtn')?.classList.remove('active');
		openVideoPresentationSurface(root);
		const currentTopicId = media.topicId ?? state.runtime.activeCourseId;
		const currentChapterIndex = media.chapterIndex ?? state.runtime.activeChapterIndex;
		if (currentTopicId) state.runtime.activeCourseId = currentTopicId;
		if (Number.isFinite(currentChapterIndex) && currentChapterIndex > 0) state.runtime.activeChapterIndex = currentChapterIndex;
		if (media?.pictureInPictureExitCleanup === stopObservingExit) media.pictureInPictureExitCleanup = undefined;
		if (wasPlaying) {
			const sourceGeneration = media.sourceGeneration;
			beginVideoPictureInPictureExitPlaybackHandoff(video, () => {
				if (mountedFor(root, 'video') !== media || media.element !== video || media.sourceGeneration !== sourceGeneration || video.ended || !media.element.paused) return;
				void playMediaPlayer(root, 'video');
			});
			// Start immediately in the browser exit callback, then cover WebKit's
			// late inline teardown pause without replacing the PiP video element.
		} else pauseMediaPlayer(root, 'video');
      }, () => {
		if (media && mountedFor(root, 'video') === media && media.element === video) showVideoPictureInPictureReturnSurface(root);
      });
		if (media) media.pictureInPictureExitCleanup = stopObservingExit;
			// PiP cannot start over a fullscreen session: the standard API rejects
			// while document fullscreen owns the presentation, and WebKit throws
			// during the switch. Always begin the request from the inline surface.
			await exitDocumentFullscreen(root);
			// WebKit anchors its floating-window enter/exit animation at the
			// inline media element's rect. Detach before requesting so that rect
			// is already the docked PiP corner instead of mid-screen; a request
			// that completes without a session re-opens the surface below.
			detachVideoPresentationSurface(root);
			let active: boolean;
			try {
				active = await toggleVideoPictureInPicture(root);
			} catch (requestError) {
				if (requestError instanceof DOMException && requestError.name === 'NotSupportedError') throw requestError;
				// Chromium can also reject the first request while a just-exited
				// fullscreen is still unwinding. Settle briefly and retry once
				// before reporting the failure to the user.
				await exitDocumentFullscreen(root);
				await new Promise((resolve) => setTimeout(resolve, 300));
				active = await toggleVideoPictureInPicture(root);
			}
      const button = root.querySelector<HTMLElement>("#vfPipBtn");
      button?.classList.toggle("active", active);
      if (active) {
			detachVideoPresentationSurface(root);
			// The entry anchor was read at request time; release the corner dock
			// so a later restore flight grows toward the full-screen surface.
			undockVideoPresentationSurface(root);
      } else {
        // Leaving PiP can complete synchronously in WebKit. It has already
        // restored the page through the observer, so only detach if no event
        // will follow. Re-opening here is idempotent and also recovers the
        // pre-request detach when no session actually started.
        stopObservingExit();
		if (media?.pictureInPictureExitCleanup === stopObservingExit) media.pictureInPictureExitCleanup = undefined;
		openVideoPresentationSurface(root);
      }
    } catch (error) {
      stopObservingExit();
		if (media?.pictureInPictureExitCleanup === stopObservingExit) media.pictureInPictureExitCleanup = undefined;
		openVideoPresentationSurface(root);
      const errorName = error instanceof DOMException ? error.name : '';
		if (errorName === 'NotSupportedError' && isStandaloneWebApp() && video && hasWebKitPictureInPictureEvidence(video)) {
			offerBrowserPictureInPicturePath(state);
		} else {
			recordRuntimeError(state.runtime, error);
			showToast({ type: 'error', message: 'Picture-in-picture could not start.' });
		}
    }
  }
  if (normalized.includes("shareVfChapter")) {
    const url = videoShareUrl(state);
    try {
      if ("share" in navigator && typeof navigator.share === "function") {
        await navigator.share({
          title: state.runtime.activeCourseId || "DeepMox",
          url,
        });
        showToast({ type: 'success', message: 'Share sheet opened.' });
        return;
      }
      await navigator.clipboard?.writeText(url);
      showToast({ type: "success", title: "Copied", message: "Share link copied." });
    } catch (error) {
      const name = error instanceof DOMException ? error.name : "";
      if (name === "AbortError") return;
      showToast({ type: "info", title: "Share", message: "Your browser blocked copy. Use the address bar link for this chapter." });
    }
  }
  if (normalized.includes("closeVideoFullscreen")) {
    // Mirror the video's actual play state onto the audio player so Back
    // resumes in the same state the video was in (no play→pause→play flicker).
    const video = mountedFor(root, "video");
    const wasPlaying = video ? !video.element.paused : false;
    await transferVideoProgressToAudio(root, state, { play: wasPlaying });
  }
  if (normalized.includes("stopVideoFullscreen")) {
    // The corner Close ends the session outright: stop playback, return to
    // the article, mount no audio. Back above keeps the audio hand-off.
    await closeVideoPlaybackSession(root, state);
  }
  if (normalized.includes("resumeTopicAudio")) {
	const intent = beginAudioHandoffCommandIntent(root);
    const resume = await resolveTopicResumeTarget(root, state);
	if (!resume || !isAudioHandoffIntentCurrent(root, intent)) return;
    setActiveTarget(root, state, resume.topicId, resume.chapterIndex, resume.progressPercent);
    showArticle(resume.chapterIndex, root, state.effectiveAccessPlan);
	await requestAudioPlayback(state.runtime, state.effectiveAccessPlan, root, recordRuntimeError, {}, intent);
    showPlayer(root);
  }
  if (normalized.includes("resumeTopicVideo")) {
    // Authentication does not depend on the resume target. Gate visitors
    // before reading their resume history; chapter-level policy is applied
    // again once the server-backed target is known.
    if (shouldBlockVideoPlayback(root, state, { topicId: state.runtime.activeCourseId, chapterIndex: 0 }, normalized)) return;
	const intent = beginMediaUserIntent(root);
	const resume = await resolveTopicResumeTarget(root, state);
	if (!resume || !isMediaUserIntentCurrent(root, intent)) return;
    if (shouldBlockVideoPlayback(root, state, resume, normalized)) return;
    setActiveTarget(root, state, resume.topicId, resume.chapterIndex, resume.progressPercent);
    pauseMediaPlayer(root, "audio");
    showArticle(resume.chapterIndex, root, state.effectiveAccessPlan);
    pushVideoFullscreenHistory(root);
		openVideoPresentationSurface(root);
    attachVideoPlayer(root, state.runtime.api, resume.topicId, resume.chapterIndex, state.effectiveAccessPlan);
	await requestVideoPlayback(state.runtime, state.effectiveAccessPlan, root, recordRuntimeError, intent);
  }
  if (normalized.includes("replayVideoCourse")) {
    root.querySelector<HTMLElement>("#vfReplay")?.classList.remove("active");
    root.querySelector<HTMLElement>("#videoFullscreen")?.classList.remove("replay-active");
    state.runtime.activeChapterIndex = 1;
    // Replay stays inside the open video session: rewrite the current
    // history entry rather than pushing a new chapter URL on top.
    showArticle(1, root, state.effectiveAccessPlan, { historyMode: 'replace' });
    attachVideoPlayer(root, state.runtime.api, state.runtime.activeCourseId, 1, state.effectiveAccessPlan);
    await requestVideoPlayback(state.runtime, state.effectiveAccessPlan, root, recordRuntimeError);
		openVideoPresentationSurface(root);
  }
}

/**
 * Toggle native browser fullscreen for the video player.
 *
 * Prefer fullscreen ownership on the application's #videoFullscreen container.
 * iPhone Safari has no container API; its mounted WebKit video fallback is
 * entered in the original tap stack and observed as a separate native session.
 */
async function toggleVideoFullscreenApi(root: Root): Promise<void> {
  const doc = root instanceof Document ? root : root.ownerDocument ?? document;
	const webkitDocument = doc as Document & {
		webkitFullscreenElement?: Element | null;
		webkitFullscreenEnabled?: boolean;
		webkitExitFullscreen?: () => Promise<void>;
	};

  // Exit a container fullscreen session when one is active.
  if (doc.fullscreenElement || webkitDocument.webkitFullscreenElement) {
    if (doc.fullscreenElement) await doc.exitFullscreen?.()?.catch(() => {});
    else await webkitDocument.webkitExitFullscreen?.()?.catch(() => {});
    return;
  }
	const media = mountedFor(root, 'video');
	const video = media?.element instanceof HTMLVideoElement ? media.element : undefined;
	const nativeVideo = video as NativeWebKitFullscreenVideo | undefined;
	if (nativeVideo?.webkitDisplayingFullscreen) {
		nativeVideo.webkitExitFullscreen?.();
		return;
	}

  const frame = doc.getElementById(playerSelectors.video.container);
  const button = doc.getElementById('vfFullscreenBtn') as HTMLButtonElement | null;
  if (!frame) return;
	const webkitFrame = frame as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> };
	const supportsStandard = doc.fullscreenEnabled !== false
		&& typeof frame.requestFullscreen === 'function'
		&& typeof doc.exitFullscreen === 'function';
	const supportsWebKit = webkitDocument.webkitFullscreenEnabled !== false
		&& typeof webkitFrame.webkitRequestFullscreen === 'function'
		&& typeof webkitDocument.webkitExitFullscreen === 'function';
	const supportsNativeWebKit = supportsNativeWebKitVideoFullscreen(video);
	if (!supportsStandard && !supportsWebKit && !supportsNativeWebKit) {
		if (button) {
			button.hidden = true;
			button.setAttribute('aria-hidden', 'true');
		}
		return;
	}
	let requestRejected = false;
  if (supportsStandard) {
    try {
      await frame.requestFullscreen();
      return;
    } catch {
		requestRejected = true;
    }
  }
	// Legacy container fullscreen is asynchronous on WebKit. When a mounted
	// native-video fallback exists, entering it must happen before that await.
	if (supportsNativeWebKit && nativeVideo?.webkitEnterFullscreen) {
		const stopObservingNativeFullscreen = media ? observeNativeWebKitFullscreen(root, media, video!) : () => undefined;
		try {
			nativeVideo.webkitEnterFullscreen();
			return;
		} catch {
			stopObservingNativeFullscreen();
			requestRejected = true;
		}
	}
  if (supportsWebKit) {
    try {
			await webkitFrame.webkitRequestFullscreen!();
			return;
		} catch {
			requestRejected = true;
		}
  }
	if (requestRejected) showToast({ type: 'info', message: 'Browser fullscreen could not start. Try again from the video controls.' });
}
